import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'store/slices/authSlice';
import { toggleTheme } from 'store/slices/themeSlice';
import { RootState } from 'store';
import { NavbarAction as NavbarActionType } from '../../types/navigation.types';
import { logoutUser } from 'api/authApi';
import Files from 'features/files/pages/Files';
import useIsMobile from 'hooks/useIsMobile';
import ProfileCard from 'components/ProfileSettings/ProfileCard';
import AccountSettingsDialog from 'components/ProfileSettings/AccountSettingsDialog';

interface NavbarActionProps {
  action: NavbarActionType;
}

const NavbarAction: React.FC<NavbarActionProps> = ({ action }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logoutUser();
      dispatch(logout());
      console.log('Logout success');
      window.location.reload();
      // navigate('/auth/login');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  if (action.id === 'avatar') {
    if (isMobile && location.pathname === '/profile-settings/mobile') {
      return null;
    }

    const displayName = action.user?.displayName;
    const username = action.user?.username;
    const truncatedDisplayName =
      displayName && displayName.length > 7
        ? `${displayName.slice(0, 7)}...`
        : displayName;

    const handleAvatarClick = () => {
      if (isMobile) {
        navigate('/profile-settings/mobile');
      } else {
        setIsDropdownOpen((prev) => !prev);
      }
    };

    const handleOpenSettings = () => {
      setIsSettingsOpen(true);
      setIsDropdownOpen(false);
    };

    return (
      <div className="relative flex items-center gap-2 z-50" ref={dropdownRef}>
        <button
          onClick={handleAvatarClick}
          className="flex items-center justify-center w-8 h-8 bg-primary text-white rounded-full overflow-hidden"
        >
          {action.user?.imgUrl ? (
            <img
              src={action.user.imgUrl}
              alt={action.user.displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML =
                  action.user!.displayName?.charAt(0).toUpperCase() || '';
              }}
            />
          ) : (
            <span>{action.user?.username.charAt(0).toUpperCase()}</span>
          )}
        </button>
        {!isMobile && (
          <>
            <span className="text-sm font-normal text-text-primary opacity-[62.75%]">
              {truncatedDisplayName}
            </span>
            {isDropdownOpen && (
              <ProfileCard
                username={username}
                displayName={displayName}
                onClose={() => setIsDropdownOpen(false)}
                onOpenSettings={handleOpenSettings}
              />
            )}

            <AccountSettingsDialog
              isOpen={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
            />
          </>
        )}
      </div>
    );
  }

  if (action.id === 'notifications') {
    // return <NotificationIcon />;
    return <></>;
  }

  if (action.id === 'files') {
    return <Files />;
  }

  if (action.type === 'theme') {
    const Icon = isDarkMode ? Icons.Lightbulb : Icons.Moon;
    return (
      <button
        className="hover:bg-neutral rounded-lg p-2"
        onClick={() => dispatch(toggleTheme())}
      >
        <Icon className="w-4 h-4 text-primary" />
      </button>
    );
  }

  if (action.type === 'icon' && action.icon) {
    const Icon = Icons[action.icon as keyof typeof Icons] as React.ElementType;
    return (
      <button
        className="hover:bg-gray-100 rounded-lg"
        onClick={() => action.link && navigate(action.link)}
      >
        <Icon className="w-4 h-4 text-primary" />
      </button>
    );
  }

  if (action.type === 'button') {
    return (
      <button
        onClick={() => {
          navigate('/auth/login');
        }}
        className="bg-primary text-white text-[13px] font-medium px-3 py-1 rounded-[4px] transition-colors"
      >
        {action.text}
      </button>
    );
  }

  return null;
};

export default NavbarAction;
